import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { X, Share, Link, Copy, Check, Facebook, Twitter, Instagram } from 'lucide-react';

const ShareModal = ({ 
    isOpen, 
    onClose, 
    mediaTitle, 
    mediaType = 'song', 
    mediaUrl, 
    username, 
    mediaId,
    // New enhanced props
    description,
    artistName,
    imageUrl,
    duration,
    tags,
    // Full override for the share message — bypasses the mediaType-specific
    // templates below entirely (used when a page needs its own custom wording,
    // e.g. events).
    shareText: shareTextOverride
}) => {
    const [copied, setCopied] = useState(false);

    if (!isOpen) return null;

    // Construct the full URL for sharing
    const baseUrl = window.location.origin;
    const shareUrl = mediaUrl || `${baseUrl}/view/${mediaType}/${username}/${mediaId}`;

    // Create richer sharing text based on content type
    let shareText = shareTextOverride || `Check out "${mediaTitle}" on TheDeepWave .com!`;
    let shareDescription = description || `Amazing ${mediaType} from The Deep Wave community.`;

    if (shareTextOverride) {
        // Skip the per-type templates below entirely.
    } else if (mediaType === 'audio' && artistName) {
        shareText = `Check out "${mediaTitle}" by ${artistName} on TheDeepWave .com!`;
        shareDescription = description || `A fantastic track by ${artistName}. ${duration ? `Duration: ${duration}` : ''}`;
    } else if (mediaType === 'video' && artistName) {
        shareText = `Check out "${mediaTitle}" by ${artistName} on TheDeepWave .com!`;
        shareDescription = description || `Amazing video content by ${artistName}. ${duration ? `Duration: ${duration}` : ''}`;
    } else if (mediaType === 'image' && artistName) {
        shareText = `🎨 Check out "${mediaTitle}" by ${artistName} on TheDeepWave .com!`;
        shareDescription = description || `Beautiful artwork by ${artistName}.`;
    } else if (mediaType === 'post' && artistName) {
        shareText = `💬 Read this post by ${artistName} on TheDeepWave .com!`;
        shareDescription = description || `Interesting thoughts shared by ${artistName}.`;
    } else if (mediaType === 'album' && artistName) {
        shareText = `💿 Check out the album "${mediaTitle}" by ${artistName} on TheDeepWave .com!`;
        shareDescription = description || `An album by ${artistName} on The Deep Wave.`;
    } else if (mediaType === 'profile' && username) {
        shareText = `👤 Check out ${mediaTitle}'s profile on TheDeepWave .com!`;
        shareDescription = description || `Discover amazing content from ${mediaTitle}.`;
    }

    const handleCopyLink = async () => {
        const fullShareText = `${shareText}\n\n${shareUrl}`;
        
        try {
            await navigator.clipboard.writeText(fullShareText);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy link:', err);
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = fullShareText;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const shareToFacebook = () => {
        const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
        window.open(fbUrl, '_blank', 'width=600,height=400');
    };

    const shareToTwitter = () => {
        const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
        window.open(twitterUrl, '_blank', 'width=600,height=400');
    };

    const shareToInstagram = () => {
        // Instagram doesn't have direct URL sharing, so we'll copy the link and show instructions
        handleCopyLink();
        alert('Link copied! Instagram doesn\'t support direct sharing, but you can paste this link in your Instagram story or bio.');
    };

    const shareViaWebAPI = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: mediaTitle,
                    text: shareText,
                    url: shareUrl,
                });
            } catch (err) {
                console.error('Error sharing:', err);
            }
        } else {
            // Fallback to copy link
            handleCopyLink();
        }
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const modalContent = (
        <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4 overflow-y-auto"
            onClick={handleBackdropClick}
        >
            <div className="glass-card max-w-md w-full max-h-[85vh] flex flex-col border border-blue-500/30 relative animate-fade-in">
                {/* Close Button */}
                <button
                    onClick={onClose}
                    aria-label="Close"
                    className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors z-10"
                >
                    <X className="w-5 h-5" />
                </button>

                {/* Header */}
                <div className="p-4 sm:p-6 border-b border-gray-700/50 flex-shrink-0">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg flex items-center justify-center">
                            <Share className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-white">Share {mediaType}</h2>
                            <p className="text-sm text-gray-400">Spread the music</p>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="p-4 sm:p-6 overflow-y-auto flex-1">
                    <div className="text-center mb-4">
                        {/* Media Preview */}
                        {imageUrl ? (
                            <div className="w-16 h-16 mx-auto mb-3 rounded-lg overflow-hidden border-2 border-blue-500/30">
                                <img
                                    src={imageUrl}
                                    alt={mediaTitle}
                                    width="64"
                                    height="64"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        ) : (
                            <div className="text-3xl mb-3">
                                {mediaType === 'audio' ? '🎵' :
                                 mediaType === 'video' ? '🎬' :
                                 mediaType === 'image' ? '🖼️' :
                                 mediaType === 'post' ? '💬' :
                                 mediaType === 'profile' ? '👤' :
                                 mediaType === 'event' ? '📅' :
                                 mediaType === 'album' ? '💿' : '📄'}
                            </div>
                        )}

                        <p className="text-gray-300 mb-1">
                            <span className="font-semibold text-white">"{mediaTitle}"</span>
                        </p>

                        {artistName && (
                            <p className="text-sm text-blue-400 mb-1">
                                by {artistName}
                            </p>
                        )}

                        {duration && (
                            <p className="text-xs text-gray-500 mb-1">
                                Duration: {duration}
                            </p>
                        )}

                        {shareDescription && (
                            <p className="text-sm text-gray-400 line-clamp-2">
                                {shareDescription}
                            </p>
                        )}

                        {tags && tags.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2 justify-center">
                                {tags.slice(0, 3).map((tag, index) => (
                                    <span key={index} className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full">
                                        #{tag}
                                    </span>
                                ))}
                                {tags.length > 3 && (
                                    <span className="px-2 py-1 bg-gray-500/20 text-gray-400 text-xs rounded-full">
                                        +{tags.length - 3} more
                                    </span>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Copy Link */}
                    <div className="bg-gray-800/50 border border-gray-600/50 rounded-xl p-3 mb-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Link className="w-4 h-4 text-gray-400" />
                                <p className="text-sm text-white font-medium">Copy Link</p>
                            </div>
                            <button
                                onClick={handleCopyLink}
                                className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg transition-colors text-sm"
                            >
                                {copied ? (
                                    <>
                                        <Check className="w-3.5 h-3.5" />
                                        Copied!
                                    </>
                                ) : (
                                    <>
                                        <Copy className="w-3.5 h-3.5" />
                                        Copy
                                    </>
                                )}
                            </button>
                        </div>
                        <div className="mt-2 p-2 bg-gray-900/50 rounded-lg">
                            <p className="text-xs text-blue-400 italic truncate">"{shareText}"</p>
                        </div>
                    </div>

                    {/* Social Media Options */}
                    <div>
                        <h3 className="text-sm font-medium text-gray-300 mb-2">Share on social media</h3>

                        <div className={`grid gap-2 ${navigator.share ? 'grid-cols-4' : 'grid-cols-3'}`}>
                            <button
                                onClick={shareToFacebook}
                                aria-label="Share on Facebook"
                                className="flex flex-col items-center gap-1.5 p-2.5 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 rounded-lg transition-colors"
                            >
                                <Facebook className="w-5 h-5 text-blue-400" />
                                <span className="text-[11px] text-gray-300">Facebook</span>
                            </button>

                            <button
                                onClick={shareToTwitter}
                                aria-label="Share on Twitter"
                                className="flex flex-col items-center gap-1.5 p-2.5 bg-sky-600/20 hover:bg-sky-600/30 border border-sky-500/30 rounded-lg transition-colors"
                            >
                                <Twitter className="w-5 h-5 text-sky-400" />
                                <span className="text-[11px] text-gray-300">Twitter</span>
                            </button>

                            <button
                                onClick={shareToInstagram}
                                aria-label="Share on Instagram"
                                className="flex flex-col items-center gap-1.5 p-2.5 bg-blue-700/20 hover:bg-blue-700/30 border border-blue-600/30 rounded-lg transition-colors"
                            >
                                <Instagram className="w-5 h-5 text-blue-400" />
                                <span className="text-[11px] text-gray-300">Instagram</span>
                            </button>

                            {navigator.share && (
                                <button
                                    onClick={shareViaWebAPI}
                                    aria-label="More share options"
                                    className="flex flex-col items-center gap-1.5 p-2.5 bg-gray-600/20 hover:bg-gray-600/30 border border-gray-500/30 rounded-lg transition-colors"
                                >
                                    <Share className="w-5 h-5 text-gray-300" />
                                    <span className="text-[11px] text-gray-300">More</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return isOpen ? ReactDOM.createPortal(modalContent, document.body) : null;
};

export default ShareModal;